import { useCallback, useEffect, useRef, useState } from "react";
import { forceElementDuration, positiveFiniteDuration } from "@/shared/audio/mediaDuration";

function resolveDuration(mediaDuration, knownDuration) {
  return positiveFiniteDuration(mediaDuration) || positiveFiniteDuration(knownDuration);
}

export function useAudioControls({
  src,
  playbackRate = 1,
  disabled = false,
  urlLoading = false,
  knownDurationSeconds = 0,
}) {
  const audioRef = useRef(null);
  const playbackRateRef = useRef(playbackRate);
  const knownDurationRef = useRef(0);
  const discoveringRef = useRef(false);
  const discoveryAttemptRef = useRef(false);
  const discoveryAbortRef = useRef(null);
  const suppressMediaErrorRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);

  const hasSrc = Boolean(src);
  playbackRateRef.current = playbackRate;

  const applyPlaybackRate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = playbackRateRef.current;
  }, []);

  useEffect(() => {
    knownDurationRef.current = positiveFiniteDuration(knownDurationSeconds);
    if (discoveringRef.current) return;
    setDuration((current) => {
      const resolved = resolveDuration(audioRef.current?.duration, knownDurationRef.current);
      return resolved || current;
    });
  }, [knownDurationSeconds]);

  useEffect(() => {
    const controller = new AbortController();
    discoveryAbortRef.current = controller;
    discoveryAttemptRef.current = false;
    discoveringRef.current = false;
    suppressMediaErrorRef.current = false;
    setCurrentTime(0);
    setPlaying(false);
    setMediaReady(false);
    setDuration(resolveDuration(0, knownDurationRef.current));
    return () => {
      controller.abort();
      discoveringRef.current = false;
      suppressMediaErrorRef.current = false;
    };
  }, [src]);

  useEffect(() => {
    applyPlaybackRate();
  }, [playbackRate, applyPlaybackRate]);

  const onTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el || discoveringRef.current) return;
    const next = el.currentTime;
    if (!Number.isFinite(next) || next < 0 || next > 24 * 60 * 60) return;
    setCurrentTime(next);
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    applyPlaybackRate();

    const direct = positiveFiniteDuration(el.duration);
    if (direct) {
      setDuration(direct);
      setMediaReady(true);
      return;
    }

    const known = knownDurationRef.current;
    if (known) setDuration(known);
    if (discoveringRef.current) return;
    if (discoveryAttemptRef.current) {
      setMediaReady(true);
      return;
    }

    const signal = discoveryAbortRef.current?.signal;
    discoveryAttemptRef.current = true;
    discoveringRef.current = true;
    suppressMediaErrorRef.current = true;

    forceElementDuration(el, { signal }).then(async (found) => {
      if (signal?.aborted) return;
      suppressMediaErrorRef.current = false;

      const node = audioRef.current;
      if (node?.error) {
        try { node.load(); } catch { /* reload after a failed duration probe */ }
      } else if (node && node.currentTime > 0.05) {
        await new Promise((resolve) => {
          let timer;
          const done = () => {
            node.removeEventListener('seeked', done);
            clearTimeout(timer);
            resolve();
          };
          node.addEventListener('seeked', done);
          timer = setTimeout(done, 500);
          try { node.currentTime = 0; } catch { done(); }
        });
      }

      if (signal?.aborted) return;
      discoveringRef.current = false;
      applyPlaybackRate();
      setCurrentTime(0);
      setDuration(resolveDuration(found || audioRef.current?.duration, knownDurationRef.current));
      setMediaReady(true);
    });
  }, [applyPlaybackRate]);

  const onDurationChange = useCallback(() => {
    if (discoveringRef.current) return;
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = playbackRateRef.current;
    setDuration(resolveDuration(el.duration, knownDurationRef.current));
  }, []);

  const onCanPlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || discoveringRef.current || !positiveFiniteDuration(el.duration)) return;
    setMediaReady(true);
  }, []);

  const onEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  const togglePlay = useCallback(() => {
    if (disabled || urlLoading || !hasSrc || !mediaReady) return;
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    void el.play().then(() => {
      el.playbackRate = playbackRateRef.current;
      setPlaying(true);
    }).catch(() => setPlaying(false));
  }, [playing, disabled, urlLoading, hasSrc, mediaReady]);

  const onSeek = useCallback(
    (e) => {
      if (disabled || urlLoading || !mediaReady) return;
      const el = audioRef.current;
      if (!el || !duration) return;
      const t = Number(e.target.value);
      el.currentTime = t;
      setCurrentTime(t);
    },
    [duration, disabled, urlLoading, mediaReady]
  );

  const onPlayEvt = useCallback(() => setPlaying(true), []);
  const onPauseEvt = useCallback(() => setPlaying(false), []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("durationchange", onDurationChange);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("ended", onEnded);
    el.addEventListener("play", onPlayEvt);
    el.addEventListener("pause", onPauseEvt);
    if (el.readyState >= 1) onLoadedMetadata();
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("durationchange", onDurationChange);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("play", onPlayEvt);
      el.removeEventListener("pause", onPauseEvt);
    };
  }, [src, onTimeUpdate, onLoadedMetadata, onDurationChange, onCanPlay, onEnded, onPlayEvt, onPauseEvt]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (disabled) {
      el.pause();
      setPlaying(false);
    }
  }, [disabled]);

  const maxDuration = duration > 0 ? duration : 1;
  const seekValue = duration > 0 ? Math.min(currentTime, duration) : 0;
  const seekDisabled =
    disabled || urlLoading || !hasSrc || !mediaReady || !duration;
  const progressPct =
    duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

  return {
    audioRef,
    duration,
    currentTime,
    playing,
    mediaReady,
    hasSrc,
    togglePlay,
    onSeek,
    maxDuration,
    seekValue,
    seekDisabled,
    progressPct,
    suppressMediaErrorRef,
  };
}
